import React, { forwardRef, useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { useFelaEnhanced } from '@ackee/fela';

import { DynamicContainer, Text, TextTypes, ContainerType, ColorLine, colorLineKeyframes } from 'modules/ui';
import { routes } from 'config/routes';
import { useStars } from 'utils/hooks';

import { Article } from './Article';
import { PixelatedBackgroundContext } from '../PixelatedBackgroundWrapper';
import { BackgroundMode } from '../PixelatedBackgroundWrapper/constants';

import * as felaRules from './OurServices.rules';
import { StatsContainer } from './StatsContainer';

export const OurServices = forwardRef<HTMLElement>((props, ref) => {
    const backgroundMode = useContext(PixelatedBackgroundContext);
    const { rules, styles, theme } = useFelaEnhanced(felaRules, { backgroundMode });
    const { colorLines } = useStars();

    return (
        <DynamicContainer
            extend={{
                container: felaRules.container,
            }}
            as={ContainerType.SECTION}
            ref={ref}
            backgroundMode={backgroundMode}
        >
            <DynamicContainer isFluid columns={12} extend={{ container: felaRules.noPaddingBottom }}>
                <DynamicContainer backgroundMode={backgroundMode} extend={{ container: felaRules.contentContainer }}>
                    <div className={styles.leftColumn}>
                        {/* @ts-expect-error */}
                        <Text
                            as="h2"
                            type={TextTypes.SECTION}
                            extend={{
                                text: rules.sectionTitle,
                            }}
                            backgroundMode={backgroundMode}
                        >
                            <FormattedMessage id="homepage.ourServices.title" />
                        </Text>
                        <DynamicContainer extend={{ container: felaRules.extendArticlesContainer }}>
                            <svg
                                width="174"
                                height="102"
                                viewBox="0 0 174 102"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className={styles.imgBefore}
                            >
                                <path
                                    d="M173 101.5L173 38L135.5 0.999997L0.499994 1"
                                    stroke={
                                        backgroundMode === BackgroundMode.Dark ? theme.colors.white : theme.colors.black
                                    }
                                    strokeWidth="2"
                                />
                            </svg>

                            <Article
                                title="homepage.ourServices.mobileApps.title"
                                text="homepage.ourServices.mobileApps.text"
                                link={routes['/app-development']}
                                anchor="#mobile"
                            />

                            <Article
                                title="homepage.ourServices.webApps.title"
                                text="homepage.ourServices.webApps.text"
                                link={routes['/app-development']}
                                anchor="#web"
                            />

                            <Article
                                title="homepage.ourServices.design.title"
                                text="homepage.ourServices.design.text"
                                link={routes['/app-development']}
                                anchor="#design"
                            />

                            <svg
                                width="407"
                                height="21"
                                viewBox="0 0 407 21"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M406.5 19.1004H75.9604L57.86 1H0"
                                    stroke={
                                        backgroundMode === BackgroundMode.Dark ? theme.colors.white : theme.colors.black
                                    }
                                    strokeWidth="2"
                                    strokeMiterlimit="10"
                                />
                            </svg>
                        </DynamicContainer>
                    </div>
                    <StatsContainer />
                </DynamicContainer>
            </DynamicContainer>
            {backgroundMode === BackgroundMode.Dark && (
                <>
                    <ColorLine keyframe={colorLineKeyframes.leftTranslate} {...colorLines[1]} />
                    <ColorLine keyframe={colorLineKeyframes.rightTranslate} {...colorLines[2]} />
                    <ColorLine keyframe={colorLineKeyframes.rightTranslate} {...colorLines[3]} />
                </>
            )}
        </DynamicContainer>
    );
});
